import _ from 'lodash';
import Initialiser from 'initialiser';
import { cloneDeep } from 'utils/immutable';
import { logout } from 'ducks/modules/user';
import constants from 'utils/constants';
import api from 'utils/api';

// ACTION TYPES
const START_FETCH_DASHBOARD_SUMMARY = 'START_FETCH_DASHBOARD_SUMMARY';
const UPDATED_DASHBOARD_SUMMARY = 'UPDATED_DASHBOARD_SUMMARY';
const START_FETCH_DASHBOARD_SUMMARY_DELTA = 'START_FETCH_DASHBOARD_SUMMARY_DELTA';
const UPDATED_DASHBOARD_SUMMARY_DELTA = 'UPDATED_DASHBOARD_SUMMARY_DELTA';
const START_FETCH_DASHBOARD_ACTIONS = 'START_FETCH_DASHBOARD_ACTIONS';
const UPDATED_DASHBOARD_ACTIONS = 'UPDATED_DASHBOARD_ACTIONS';
const START_FETCH_DASHBOARD_MATCHING = 'START_FETCH_DASHBOARD_MATCHING';
const UPDATED_DASHBOARD_MATCHING = 'UPDATED_DASHBOARD_MATCHING';

// SYNC ACTION CREATORS
export function startFetchDashboardSummary() {
    return { type: START_FETCH_DASHBOARD_SUMMARY };
}
export function updatedDashboardSummary(stats, range) {
    return { type: UPDATED_DASHBOARD_SUMMARY, stats, range };
}
export function startFetchDashboardSummaryDelta() {
    return { type: START_FETCH_DASHBOARD_SUMMARY_DELTA };
}
export function updatedDashboardSummaryDelta(deltas, range) {
    return { type: UPDATED_DASHBOARD_SUMMARY_DELTA, deltas, range };
}
export function startFetchDashboardActions() {
    return { type: START_FETCH_DASHBOARD_ACTIONS };
}
export function updatedDashboardActions(actions) {
    return { type: UPDATED_DASHBOARD_ACTIONS, actions };
}
export function startFetchDashboardMatching() {
    return { type: START_FETCH_DASHBOARD_MATCHING };
}
export function updatedDashboardMatching(stats) {
    return { type: UPDATED_DASHBOARD_MATCHING, stats };
}

// ASYNC ACTION CREATORS
export function fetchDashboardSummary(bkUserId, range) {
    return async (dispatch) => {
        dispatch(startFetchDashboardSummary());
        try {
            const response = await api.fetchDashboardStats(bkUserId, range);
            for (const key in response) {
                if (isNaN(response[key])) {
                    throw new Error('Catalogue summary must be a number.');
                }
                response[key] = Number(response[key]);
            }
            dispatch(updatedDashboardSummary(response, range));
        } catch (err) {
            console.error('Error fetching catalogue summary: ', err);
            dispatch(updatedDashboardSummary({}));
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
            } else if (err && err.status === constants.HttpCodes.FORBIDDEN) {
                return Initialiser.history.replace('/forbidden');
            } else if (err && err.status === constants.HttpCodes.BAD_REQUEST) {
                return Initialiser.history.replace('/notfound');
            }
        }
    };
}

export function fetchDashboardSummaryDelta(bkUserId, range) {
    return async (dispatch) => {
        dispatch(startFetchDashboardSummaryDelta());
        try {
            const response = await api.fetchDashboardStats(bkUserId, range);
            for (const key in response) {
                if (isNaN(response[key])) {
                    throw new Error('Catalogue summary must be a number.');
                }
                response[key] = Number(response[key]);
            }
            dispatch(updatedDashboardSummaryDelta(response, range));
        } catch (err) {
            console.error('Error fetching catalogue summary: ', err);
            dispatch(updatedDashboardSummaryDelta({}));
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
            } else if (err && err.status === constants.HttpCodes.FORBIDDEN) {
                return Initialiser.history.replace('/forbidden');
            } else if (err && err.status === constants.HttpCodes.BAD_REQUEST) {
                return Initialiser.history.replace('/notfound');
            }
        }
    };
}

export function fetchDashboardActions(bkUserId) {
    return async (dispatch) => {
        dispatch(startFetchDashboardActions());
        try {
            const response = await api.fetchDashboardActions(bkUserId);
            // TODO: require some validation
            dispatch(updatedDashboardActions(response));
        } catch (err) {
            console.error('Error fetching suggested actions: ', err);
            dispatch(updatedDashboardActions({}));
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
            } else if (err && err.status === constants.HttpCodes.FORBIDDEN) {
                return Initialiser.history.replace('/forbidden');
            } else if (err && err.status === constants.HttpCodes.BAD_REQUEST) {
                return Initialiser.history.replace('/notfound');
            }
        }
    };
}

export function fetchDashboardMatching(bkUserId, range = 30) {
    return async (dispatch) => {
        dispatch(startFetchDashboardMatching());
        try {
            const response = await api.fetchDashboardStats(bkUserId, range);
            for (const key in response) {
                if (isNaN(response[key])) {
                    throw new Error('Matching stats must be a number.');
                }
                response[key] = Number(response[key]);
            }
            dispatch(updatedDashboardMatching(response));
        } catch (err) {
            console.error('Error fetching matching stats: ', err);
            dispatch(updatedDashboardMatching({}));
            if (err && err.status === constants.HttpCodes.UNAUTHORIZED) {
                return dispatch(logout(err.data === constants.ServerCodes.TOKEN_EXPIRED));
            } else if (err && err.status === constants.HttpCodes.FORBIDDEN) {
                return Initialiser.history.replace('/forbidden');
            } else if (err && err.status === constants.HttpCodes.BAD_REQUEST) {
                return Initialiser.history.replace('/notfound');
            }
        }
    };
}

// REDUCER
export default function reducer(state = Initialiser.initialState.dashboard, action) {
    switch (action.type) {
        case UPDATED_DASHBOARD_SUMMARY: {
            const dashboard = cloneDeep(state);
            dashboard.summary = _.pick(action.stats, [
                'total',
                'sync',
                'conflicts',
                'mentions',
                'claims',
                'delta_total',
                'delta_sync',
                'delta_conflicts',
                'delta_mentions',
                'delta_claims',
            ]);
            dashboard.summary.range = action.range;
            return dashboard;
        }

        case UPDATED_DASHBOARD_SUMMARY_DELTA: {
            const dashboard = cloneDeep(state);
            dashboard.summary = _.pick(action.deltas, [
                'total',
                'sync',
                'conflicts',
                'mentions',
                'claims',
                'delta_total',
                'delta_sync',
                'delta_conflicts',
                'delta_mentions',
                'delta_claims',
            ]);
            dashboard.summary.range = action.range;
            return dashboard;
        }
        case UPDATED_DASHBOARD_ACTIONS: {
            const dashboard = cloneDeep(state);
            dashboard.actions = action.actions;
            return dashboard;
        }
        case UPDATED_DASHBOARD_MATCHING: {
            const dashboard = cloneDeep(state);
            dashboard.matching = _.pick(action.stats, ['recordings', 'delta_recordings']);
            return dashboard;
        }
        default:
            return state;
    }
}
